import { Menu } from 'lucide-react'
import Link from 'next/link'
import * as React from 'react'

import useMediaQuery from '@/hooks/use-media-query'

import BlogFileViewer from '@/components/blog/blog-file-viewer'
import CompanyChip from '@/components/common/company-chip'
import { ResolvedAvatarImage } from '@/components/common/resolved-media-image'
import MarkdownPreview from '@/components/markdown/markdown-preview'
import TableOfContents from '@/components/markdown/table-of-contents'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'

import { BlogContentType } from '@/enums/blog-content-type.enum'
import { estimateReadingTime } from '@/utils/content'
import { calculateDaysAgo } from '@/utils/date-fn'

import BlogCommentsContainer from './blog-comments-container'
import BlogActionContainer from './shared/blog-action-container'

import { BlogType } from '@/types/blog.type'

interface BlogUserContentContainerProps {
  blog: BlogType
}

function BlogHeader({ blog }: BlogUserContentContainerProps) {
  return (
    <div className="mt-10 sm:mt-20">
      <h1 className="text-3xl font-semibold md:text-5xl">{blog.title}</h1>
      <div className="my-4 pt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <Avatar className="h-12 w-12 sm:h-14 sm:w-14">
            <ResolvedAvatarImage avatar={blog.author?.avatar} alt={blog.author?.firstName || 'Author'} />
            <AvatarFallback>A</AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start justify-center">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-center text-base font-medium">
                {blog.author ? (
                  <Link href={`/user/${blog.author?.profileId || ''}`} className="hover:underline">
                    {blog.author?.firstName} {blog.author?.lastName}
                  </Link>
                ) : (
                  'FSDS Team'
                )}
              </div>
              {blog.author?.company && <CompanyChip company={blog.author.company} />}
            </div>
            <p className="text-sm font-normal text-muted-foreground">
              {blog.contentType === BlogContentType.FILE
                ? `File • ${calculateDaysAgo(blog.createdAt)} day ago`
                : `${estimateReadingTime(blog.content)} min read • ${calculateDaysAgo(blog.createdAt)} day ago`}
            </p>
          </div>
        </div>
        <BlogActionContainer blog={blog} />
      </div>
    </div>
  )
}

function TOCMobile({ content }: { content: string }) {
  return (
    <div className="fixed bottom-6 left-6 z-50">
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="secondary"
            className="flex items-center justify-center rounded-full size-8 bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
            aria-label="Open Table of Contents"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[80%] sm:w-[350px] overflow-y-auto">
          <div className="py-2">
            <TableOfContents content={content} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

function BlogContentUserSection({ content }: { content: string }) {
  const isMobile = useMediaQuery('(max-width: 768px)')
  return (
    <div className="pt-6 lg:pt-4">
      {isMobile && <TOCMobile content={content} />}

      <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full md:gap-6 lg:gap-10">
        {isMobile ? null : (
          <div className="md:w-1/4 lg:w-2/5 sticky top-24 z-10 h-fit self-start max-h-[calc(100vh-100px)] overflow-y-auto transition-all">
            <TableOfContents content={content} />
          </div>
        )}

        <article className="prose prose-sm sm:prose-base lg:prose-lg md:w-3/4 lg:w-4/5 min-w-0 overflow-hidden prose-pre:overflow-x-auto">
          <MarkdownPreview enableImagePreview={true}>{content}</MarkdownPreview>
        </article>
      </div>
    </div>
  )
}

function BlogUserContentContainer({ blog }: BlogUserContentContainerProps) {
  const isFileBlog = blog.contentType === BlogContentType.FILE

  return (
    <>
      {isFileBlog && blog.fileUrl && blog.fileName && blog.mimeType ? (
        <BlogFileContentPage blog={blog} fileName={blog.fileName} mimeType={blog.mimeType} fileUrl={blog.fileUrl} />
      ) : (
        <BlogTextContentPage blog={blog} />
      )}
    </>
  )
}

interface BlogFileContentPageProps {
  blog: BlogType
  fileName: string
  mimeType: string
  fileUrl: string
}

function BlogFileContentPage({ blog, fileName, mimeType, fileUrl }: BlogFileContentPageProps) {
  return (
    <div className="container">
      <div className="mx-auto max-w-6xl min-h-[90vh]">
        <BlogHeader blog={blog} />
        <BlogFileViewer fileName={fileName} mimeType={mimeType} fileUrl={fileUrl} />
        <BlogCommentsContainer blogId={blog.id} blogSlug={blog.slug} />
      </div>
    </div>
  )
}

function BlogTextContentPage({ blog }: BlogUserContentContainerProps) {
  return (
    <div className="container">
      <div className="mx-auto max-w-6xl min-h-[90vh]">
        <BlogHeader blog={blog} />
        <BlogContentUserSection content={blog.content} />
        <BlogCommentsContainer blogId={blog.id} blogSlug={blog.slug} />
      </div>
    </div>
  )
}

export default BlogUserContentContainer
